import type { EditorialContentCreateRequest } from "@/api/researchCms.service";

export type LawyerCreateInput = {
  contactUuid: string;
  contactName: string | null;
  companyId: string | null;
  firmName: string | null;
  officeId?: string | null;
  officeName?: string | null;
  editionId: string;
  researchBpaId: string;
  directoryId: string | null;
  /** The table-scoped view the record is being created from; absent for the untabled scope. */
  rankingTableId?: string;
};

export type LawyerCreateRequest =
  | { ok: true; request: EditorialContentCreateRequest }
  | { ok: false; reason: string };

export function buildLawyerCreateRequest(input: LawyerCreateInput): LawyerCreateRequest {
  if (!input.companyId) {
    return { ok: false, reason: "This lawyer's firm could not be determined." };
  }
  if (!input.directoryId) {
    return { ok: false, reason: "This edition is not linked to a directory." };
  }

  return {
    ok: true,
    request: {
      companyId: input.companyId,
      researchBpaId: input.researchBpaId,
      editionId: input.editionId,
      directoryId: input.directoryId,
      firmName: input.firmName ?? input.contactName ?? undefined,
      contactId: input.contactUuid,
      contactName: input.contactName ?? undefined,
      ...(input.rankingTableId ? { rankingTableId: input.rankingTableId } : {}),
      ...(input.officeId
        ? { officeId: input.officeId, officeName: input.officeName ?? undefined }
        : {}),
    },
  };
}

export function resolveLawyerCreateRequest(
  input: LawyerCreateInput | null,
  lookupFailed: boolean,
): LawyerCreateRequest | null {
  if (lookupFailed) {
    return {
      ok: false,
      reason:
        "This lawyer could not be found in the address book, so a record cannot be created here.",
    };
  }
  // A lookup that has not come back yet is not a refusal: returning null here (rather than a
  // reason) is what keeps the notice off screen while the address book request is in flight.
  if (input === null) {
    return null;
  }
  return buildLawyerCreateRequest(input);
}
